import type { CalculateResponse } from '@/types';

type Headline = {
  title: string;
  subtitle: string;
  chips: string[];
};

const STEM_NORMALIZE: Array<[string, string]> = [
  ['갑', '갑'], ['甲', '갑'],
  ['을', '을'], ['乙', '을'],
  ['병', '병'], ['丙', '병'],
  ['정', '정'], ['丁', '정'],
  ['무', '무'], ['戊', '무'],
  ['기', '기'], ['己', '기'],
  ['경', '경'], ['庚', '경'],
  ['신', '신'], ['辛', '신'],
  ['임', '임'], ['壬', '임'],
  ['계', '계'], ['癸', '계'],
];

const STEM_HEADLINE: Record<string, { title: string; subtitle: string }> = {
  갑: { title: '뿌리를 내리고 확장할 타이밍', subtitle: '기반(사람·시스템)을 먼저 고정하면, 확장이 자연스럽게 따라옵니다.' },
  을: { title: '정교하게 다듬어 수익으로 묶을 타이밍', subtitle: '브랜딩·상품·가격을 "한 번에 이해되게" 정리하면 전환이 올라갑니다.' },
  병: { title: '노출과 속도를 레버리지할 타이밍', subtitle: '빠른 실행 + 공개 루틴이 가장 큰 자산이 됩니다.' },
  정: { title: '핵심 한 점에 집중해 폭발시킬 타이밍', subtitle: '할 일을 늘리기보다, "가장 강한 한 줄"에 집중할수록 성과가 납니다.' },
  무: { title: '자산·인프라를 쌓아 방어력을 키울 타이밍', subtitle: '흔들리지 않는 기반(현금흐름/운영)을 먼저 만들면 이후가 편해집니다.' },
  기: { title: '정리·운영으로 효율을 올릴 타이밍', subtitle: '버리는 것과 자동화가 곧 성장입니다.' },
  경: { title: '결단으로 구조를 바꿀 타이밍', subtitle: '애매한 것들을 자르고 구조를 다시 짜면, 속도가 회복됩니다.' },
  신: { title: '품질·규율로 신뢰를 쌓을 타이밍', subtitle: '작은 디테일의 일관성이 "신뢰"를 만들고, 신뢰가 성과로 이어집니다.' },
  임: { title: '흐름을 확장해 판을 키울 타이밍', subtitle: '유통(채널)과 연결(파트너)을 키우면 스케일이 열립니다.' },
  계: { title: '정보·통찰로 방향을 교정할 타이밍', subtitle: '지표/피드백을 근거로 방향을 교정하면 리스크가 줄어듭니다.' },
};

function normalizeStem(dayMaster: string): string | null {
  const raw = (dayMaster || '').trim();
  for (const [token, normalized] of STEM_NORMALIZE) {
    if (raw.startsWith(token)) return normalized;
  }
  return null;
}

/**
 * 결제 전용 "개인화 헤드라인"
 * - 절대 돈/행동/캘린더/PDF 상세 노출 금지
 * - 결정론적 룰(일간)로만 생성
 */
export function buildPreviewHeadline(
  calc: CalculateResponse,
  targetYear = 2026,
  name?: string
): Headline {
  const stem = normalizeStem((calc as any).day_master) ?? '갑';
  const info = STEM_HEADLINE[stem] ?? STEM_HEADLINE['갑'];

  const chips: string[] = [];
  const dm = (calc as any).day_master ? `일간 ${(calc as any).day_master}` : null;
  const el = (calc as any).day_master_element ? `오행 ${(calc as any).day_master_element}` : null;
  const gyeok = (calc as any).gyeokguk ? `격국 ${(calc as any).gyeokguk}` : null;
  const ten = (calc as any).month_branch_ten_god ? `월지십성 ${(calc as any).month_branch_ten_god}` : null;

  if (dm) chips.push(dm);
  if (el) chips.push(el);
  if (gyeok) chips.push(gyeok);
  if (ten) chips.push(ten);

  const who = name ? `${name}님, ` : '';
  return {
    title: `${who}${targetYear}년, ${info.title}`,
    subtitle: `${info.subtitle} (구체적인 근거/전략은 결제 후 전체 리포트에서 잠금 해제됩니다)`,
    chips,
  };
}
